import {
    MAP_WIDTH,
    MAP_HEIGHT,
    ZOOM_MASS_THRESHOLD,
    ZOOM_MIN,
    ZOOM_MAX,
    ZOOM_SMOOTHING,
    ZOOM_EXPONENT,
    MANUAL_ZOOM_MIN,
    MANUAL_ZOOM_MAX,
    MAX_PLAYER_SCREEN_RATIO,
} from "../src/shared/constants";

export class Camera {
    private x: number;
    private y: number;
    private viewportWidth: number;
    private viewportHeight: number;
    private zoom: number = 1.0;
    private manualZoomFactor: number = 1.0;
    private shakeIntensity: number = 0;
    private shakeEndTime: number = 0;
    private shakeDuration: number = 1;

    constructor(viewportWidth: number, viewportHeight: number) {
        this.viewportWidth = viewportWidth;
        this.viewportHeight = viewportHeight;
        this.x = MAP_WIDTH / 2;
        this.y = MAP_HEIGHT / 2;
        this.zoom = this.getDeviceScale();
    }

    public setViewport(width: number, height: number): void {
        this.viewportWidth = width;
        this.viewportHeight = height;
    }

    /**
     * Calcula el factor de escala base según el tamaño de la pantalla del dispositivo.
     * Evita que en pantallas pequeñas (móviles) el campo de visión sea diminuto y todo
     * parezca moverse demasiado rápido en comparación con un monitor de PC.
     */
    private getDeviceScale(): number {
        const minDim = Math.min(this.viewportWidth, this.viewportHeight);
        const maxDim = Math.max(this.viewportWidth, this.viewportHeight);
        // Referencia base: monitor estándar 1366x768
        const ratio = Math.min(maxDim / 1366, minDim / 768);
        return Math.max(0.58, Math.min(1.05, Math.pow(ratio, 0.42)));
    }

    /**
     * Adapta progresivamente el zoom de la cámara a medida que el jugador crece en masa
     * combinando la curva exponencial suave con Screen-Space Ratio Clamping estricto (máx 26% de pantalla).
     */
    public updateZoomForMass(mass: number): void {
        const safeMass = Math.max(ZOOM_MASS_THRESHOLD, Number.isFinite(mass) ? mass : ZOOM_MASS_THRESHOLD);
        const deviceScale = this.getDeviceScale();

        // 1. Curva exponencial progresiva suave desde masa 100
        const curveZoom = Math.pow(ZOOM_MASS_THRESHOLD / safeMass, ZOOM_EXPONENT) * deviceScale;

        // 2. Límite estricto de proporción en pantalla (El diámetro del jugador + halo nunca supera el 26% del lado menor del viewport)
        const worldDiameter = Math.sqrt(safeMass) * 4 * 1.18; // incluye margen de púas/llamas
        const minViewportDim = Math.max(280, Math.min(this.viewportWidth, this.viewportHeight));
        const maxAllowedScreenDiameter = minViewportDim * MAX_PLAYER_SCREEN_RATIO;
        const ratioLimitZoom = maxAllowedScreenDiameter / Math.max(1, worldDiameter);

        // Tomamos el menor de ambos para garantizar que en móviles jamás tape la pantalla
        const autoZoom = Math.max(ZOOM_MIN, Math.min(1.05 * deviceScale, Math.min(curveZoom, ratioLimitZoom)));

        const targetZoom = Math.max(
            ZOOM_MIN,
            Math.min(ZOOM_MAX, autoZoom * this.manualZoomFactor)
        );
        this.zoom += (targetZoom - this.zoom) * ZOOM_SMOOTHING;
    }

    public adjustManualZoom(delta: number): void {
        this.manualZoomFactor = Math.max(
            MANUAL_ZOOM_MIN,
            Math.min(MANUAL_ZOOM_MAX, this.manualZoomFactor + delta)
        );
    }

    public resetManualZoom(): void {
        this.manualZoomFactor = 1.0;
    }

    public setPosition(x: number, y: number): void {
        this.x = x;
        this.y = y;
    }

    public follow(targetX: number, targetY: number, smoothFactor?: number): void {
        if (smoothFactor !== undefined && smoothFactor >= 0 && smoothFactor <= 1) {
            this.x += (targetX - this.x) * smoothFactor;
            this.y += (targetY - this.y) * smoothFactor;
        } else {
            this.x = targetX;
            this.y = targetY;
        }
        // Mantener al jugador siempre centrado incluso en los bordes y esquinas del mapa
        // para que nunca quede oculto detrás de los menús del HUD.
        this.x = Math.max(0, Math.min(MAP_WIDTH, this.x));
        this.y = Math.max(0, Math.min(MAP_HEIGHT, this.y));
    }

    /**
     * Activa un temblor de cámara con decaimiento suave para dar impacto físico a combates y explosiones.
     */
    public triggerShake(intensity: number, durationMs: number): void {
        const now = performance.now();
        const remaining = Math.max(0, this.shakeEndTime - now);
        // Si ya hay un shake más fuerte activo, mantener el mayor
        if (remaining > 0 && this.shakeIntensity > intensity) return;
        this.shakeIntensity = intensity;
        this.shakeDuration = Math.max(1, durationMs);
        this.shakeEndTime = now + durationMs;
    }

    public worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
        let shakeX = 0;
        let shakeY = 0;
        const now = performance.now();
        if (now < this.shakeEndTime) {
            const progress = (this.shakeEndTime - now) / this.shakeDuration;
            const currentMag = this.shakeIntensity * progress * progress;
            shakeX = (Math.random() - 0.5) * 2 * currentMag;
            shakeY = (Math.random() - 0.5) * 2 * currentMag;
        }
        return {
            x: (worldX - this.x) * this.zoom + this.viewportWidth / 2 + shakeX,
            y: (worldY - this.y) * this.zoom + this.viewportHeight / 2 + shakeY,
        };
    }

    public screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
        return {
            x: (screenX - this.viewportWidth / 2) / this.zoom + this.x,
            y: (screenY - this.viewportHeight / 2) / this.zoom + this.y,
        };
    }

    public getZoom(): number {
        return this.zoom;
    }

    public getX(): number {
        return this.x;
    }

    public getY(): number {
        return this.y;
    }

    public getViewport(): { width: number; height: number } {
        return {
            width: this.viewportWidth,
            height: this.viewportHeight,
        };
    }
}
